import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TOAST_SUCCESS_STYLE } from 'config/constants';
import useAsyncEffect from 'use-async-effect';
import { Loader } from 'components/Loader/Loader';
import { ThemeMenuIcon } from 'components/ThemeMenuIcon/ThemeMenuIcon';
import { UserContext } from 'context/userContext';
import { intlLocaleOf } from 'config/languages';
import type { MenuIconKey } from 'lib/menuIcons';
import {
  PlayerBonus,
  PlayerProfile,
  changeEmail,
  getBonuses,
  getProfile,
  saveProfile,
} from 'services/api/responsibleService';
import { ResponsibleFrame } from './BettingLimits';
import styles from '../styles/pages/Responsible.module.css';

const CURRENCY = process.env.REACT_APP_CURRENCY ?? '';

/** Redirects a logged-out visitor; true when the page may render. */
function useLoggedIn() {
  const { loggedUser } = useContext(UserContext);
  const navigate = useNavigate();
  if (!loggedUser) {
    navigate('/login');
    return null;
  }
  return loggedUser;
}

const SETTINGS: {
  icon: MenuIconKey;
  label: string;
  hint: string;
  to: string;
}[] = [
  {
    icon: 'person',
    label: 'personal_data_label',
    hint: 'personal_data_hint',
    to: '/personal-data',
  },
  {
    icon: 'account-settings',
    label: 'change_password_label',
    hint: 'modern_hint_password',
    to: '/change-password',
  },
  {
    icon: 'contact',
    label: 'change_email_label',
    hint: 'change_email_hint',
    to: '/change-email',
  },
  {
    icon: 'deposit',
    label: 'my_bonuses_label',
    hint: 'my_bonuses_hint',
    to: '/my-bonuses',
  },
  {
    icon: 'theme',
    label: 'theme_settings_label',
    hint: 'modern_hint_theme',
    to: '/theme-settings',
  },
];

/** Everything about the player's own account, in one list. */
export const AccountSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!useLoggedIn()) return null;
  return (
    <ResponsibleFrame titleKey="account_settings_label">
      <div className={styles.menu}>
        {SETTINGS.map((s) => (
          <button
            type="button"
            key={s.to}
            className={styles.menuItem}
            onClick={() => navigate(s.to)}
          >
            <ThemeMenuIcon icon={s.icon} alt="" variant="footer" />
            <span>
              <strong>{t(s.label)}</strong>
              <small>{t(s.hint)}</small>
            </span>
            <em aria-hidden>›</em>
          </button>
        ))}
      </div>
    </ResponsibleFrame>
  );
};

const EMPTY: PlayerProfile = {
  title: '',
  first_name: '',
  last_name: '',
  email: '',
  date_of_birth: '',
  street: '',
  house_number: '',
  zip_code: '',
  city: '',
  country: '',
  phone: '',
};

const TITLES = ['title_mr', 'title_mrs', 'title_ms'];

export const PersonalData = () => {
  const { t } = useTranslation();
  const user = useLoggedIn();
  const [form, setForm] = useState<PlayerProfile | null>(null);
  const [busy, setBusy] = useState(false);

  useAsyncEffect(async () => {
    if (!user) return;
    try {
      setForm({ ...EMPTY, ...(await getProfile()) });
    } catch {
      setForm(EMPTY);
      toast(t('profile_load_error'));
    }
  }, [user?.id]);

  if (!user) return null;

  const field = (
    key: keyof PlayerProfile,
    labelKey: string,
    type: string = 'text',
    autoComplete?: string
  ) => (
    <label className={styles.field}>
      <span>{t(labelKey)}</span>
      <input
        className={styles.textInput}
        type={type}
        autoComplete={autoComplete}
        value={form?.[key] ?? ''}
        onChange={(e) => setForm({ ...form!, [key]: e.target.value })}
      />
    </label>
  );

  const submit = async () => {
    if (!form || busy) return;
    setBusy(true);
    try {
      setForm({ ...EMPTY, ...(await saveProfile(form)) });
      toast(t('profile_saved'), { style: TOAST_SUCCESS_STYLE });
    } catch {
      toast(t('profile_save_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsibleFrame titleKey="personal_data_label">
      {!form ? (
        <Loader />
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className={styles.field}>
            <span>{t('profile_title')}</span>
            <select
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            >
              <option value="">{t('select_placeholder')}</option>
              {TITLES.map((k) => (
                <option key={k} value={k}>
                  {t(k)}
                </option>
              ))}
            </select>
          </label>
          {field('first_name', 'profile_first_name', 'text', 'given-name')}
          {field('last_name', 'profile_last_name', 'text', 'family-name')}
          {field('date_of_birth', 'profile_date_of_birth', 'date', 'bday')}
          {field('street', 'profile_street', 'text', 'address-line1')}
          {field('house_number', 'profile_house_number')}
          <div className={styles.row2}>
            {field('zip_code', 'profile_zip_code', 'text', 'postal-code')}
            {field('city', 'profile_city', 'text', 'address-level2')}
          </div>
          {field('country', 'profile_country', 'text', 'country-name')}
          {field('phone', 'profile_phone', 'tel', 'tel')}
          <button type="submit" className={styles.primary} disabled={busy}>
            {t('profile_save')}
          </button>
        </form>
      )}
    </ResponsibleFrame>
  );
};

export const ChangeEmail = () => {
  const { t } = useTranslation();
  const user = useLoggedIn();
  const [current, setCurrent] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [email2, setEmail2] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useAsyncEffect(async () => {
    if (!user) return;
    try {
      setCurrent((await getProfile()).email ?? '');
    } catch {
      setCurrent('');
    }
  }, [user?.id]);

  if (!user) return null;

  const submit = async () => {
    if (busy) return;
    if (email.trim().toLowerCase() !== email2.trim().toLowerCase()) {
      toast(t('email_mismatch'));
      return;
    }
    setBusy(true);
    const res = await changeEmail(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      setCurrent(res.profile.email);
      setEmail('');
      setEmail2('');
      setPassword('');
      toast(t('email_changed'), { style: TOAST_SUCCESS_STYLE });
      return;
    }
    toast(t(`email_error_${res.code}`));
  };

  return (
    <ResponsibleFrame titleKey="change_email_label">
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className={styles.current}>
          {t('email_current')}: <strong>{current || '—'}</strong>
        </p>
        <label className={styles.field}>
          <span>{t('email_new')}</span>
          <input
            className={styles.textInput}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span>{t('email_repeat')}</span>
          <input
            className={styles.textInput}
            type="email"
            required
            value={email2}
            onChange={(e) => setEmail2(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span>{t('current_password_label')}</span>
          <input
            className={styles.textInput}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button type="submit" className={styles.primary} disabled={busy}>
          {t('email_submit')}
        </button>
      </form>
    </ResponsibleFrame>
  );
};

export const MyBonuses = () => {
  const { t, i18n } = useTranslation();
  const user = useLoggedIn();
  const [rows, setRows] = useState<PlayerBonus[] | null>(null);

  useAsyncEffect(async () => {
    if (!user) return;
    try {
      setRows(await getBonuses());
    } catch {
      setRows([]);
    }
  }, [user?.id]);

  if (!user) return null;

  return (
    <ResponsibleFrame titleKey="my_bonuses_label">
      {!rows && <Loader />}
      {rows?.length === 0 && (
        <div className={styles.empty}>
          <span aria-hidden>🎁</span>
          <p>{t('no_bonus_message')}</p>
        </div>
      )}
      {rows?.map((b) => (
        <div key={b.id} className={styles.limitCard}>
          <div className={styles.limitHead}>
            <span>
              <strong>{b.title}</strong>
              <small>
                {new Date(b.created_at).toLocaleString(
                  intlLocaleOf(i18n.language),
                  { dateStyle: 'medium', timeStyle: 'short' }
                )}
              </small>
            </span>
            <span className={styles.bonusAmount}>
              +{Number(b.amount).toFixed(2)} {CURRENCY}
            </span>
          </div>
          <small className={styles.muted}>{t('bonus_paid_note')}</small>
        </div>
      ))}
    </ResponsibleFrame>
  );
};
